import React, { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import SelectField from '@/v2/components/common/SelectField'
import TextField from '@/v2/components/common/TextField'
import Button from '@/v2/components/common/Button'
import Box from '@mui/material/Box'
import { useProviders } from '@/v2/hooks/insurance/useEligibilities'
import { providerKindOptions, specialtyOptions } from '@/v2/constants/insurance'
import { usStateOptions } from '@/v2/constants/usStates'
import {
  DeleteOutline,
  EditOutlined,
  SaveOutlined,
  CancelOutlined,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'

const DEFAULT_PROVIDER_DATA = {
  provider: '',
  taxId: '',
  npi: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipcode: '',
  kind: '',
  type: '',
  specialty: 'ORTHO',
  taxonomy: '',
  npiType: '',
  licenseNumber: '',
  licenseState: '',
}

const toFormData = provider => {
  const addressData = provider.addresses?.[0] || {}
  return {
    provider: provider.provider || '',
    firstName: provider.firstName || '',
    lastName: provider.lastName || '',
    organizationName: provider.organizationName || '',
    taxId: provider.taxId || '',
    npi: provider.npi || '',
    address1: addressData.address1 || '',
    address2: addressData.address2 || '',
    city: addressData.city || '',
    state: addressData.state || '',
    zipcode: addressData.zipcode || '',
    kind: addressData.kind || '',
    type: provider.kind || '',
    specialty: provider.specialty || 'ORTHO',
    taxonomy: provider.taxonomy || '',
    npiType: provider.npiType || '',
    licenseNumber: provider.licenseNumber || '',
    licenseState: provider.licenseState || '',
  }
}

const formatProviderOptions = (providers = []) =>
  providers.map(({ id, name }) => ({
    value: id,
    label: name,
  }))

const Provider = ({
  values = DEFAULT_PROVIDER_DATA,
  error = {},
  onChange,
  onSave,
  isAdding,
  isEdit,
  onDelete,
  onEdit,
  onCancel,
  taxonomyCodes,
  provider,
  viewOnly = false,
}) => {
  const [taxonomies, setTaxonomies] = useState(taxonomyCodes || [])
  const [selectedProvider, setSelectedProvider] = useState(
    DEFAULT_PROVIDER_DATA
  )
  const [isManualEntry, setIsManualEntry] = useState(false)
  const [backupProviderData, setBackupProviderData] = useState({})
  const { providers } = useProviders()
  const theme = useTheme()

  useEffect(() => {
    setTaxonomies(taxonomyCodes || [])
  }, [taxonomyCodes])

  useEffect(() => {
    if (provider) {
      setSelectedProvider(toFormData(provider))
    }
  }, [provider])

  useEffect(() => {
    if (isAdding) {
      setSelectedProvider({ ...DEFAULT_PROVIDER_DATA, ...values })
    }
  }, [values, isAdding])

  const setProviderData = data => {
    setSelectedProvider(prev => ({ ...prev, ...data }))
    onChange({ ...selectedProvider, ...data })
  }

  const handleCancel = () => {
    if (isAdding) {
      setSelectedProvider(DEFAULT_PROVIDER_DATA)
    } else if (provider) {
      // revert to original provider data
      setSelectedProvider(toFormData(provider))
    }
    onCancel()
  }

  const handleSelectProvider = value => {
    if (isManualEntry) return
    const selected = providers.find(p => `${p.id}` === value)
    if (selected) {
      const taxonomyOptions =
        selected.taxonomies?.map(({ code, specialty, category }) => ({
          value: code,
          label: `${code} - ${specialty || category || ''}`,
        })) || []
      setTaxonomies(taxonomyOptions)

      const addressData = selected.addresses?.[0] || {}
      setProviderData({
        provider: selected.id,
        ...(selected.npiType === 'clinic'
          ? { organizationName: selected.name }
          : { firstName: selected.firstName, lastName: selected.lastName }),
        taxId: selected.taxId || '',
        npi: selected.npi || '',
        type: selected.type || '',
        specialty: selected.specialty || 'ORTHO',
        taxonomy: selected.taxonomy || '',
        npiType:
          selected.providerType === 'clinic' ? 'organization' : 'individual',
        // Only doctors carry a license; picking the clinic clears it.
        licenseNumber: selected.licenseNumber || '',
        licenseState: selected.licenseState || '',
        ...addressData,
      })
    }
  }

  const handleFieldChange = field => event => {
    const value = event.target ? event.target.value : event
    setProviderData({ [field]: value })
  }

  const handleSave = () => {
    onSave(selectedProvider)
  }

  const handleManualEntryToggle = () => {
    if (isManualEntry) {
      setSelectedProvider(backupProviderData)
      setBackupProviderData({})
    } else {
      setBackupProviderData(selectedProvider)
      setProviderData(DEFAULT_PROVIDER_DATA)
    }
    setIsManualEntry(prev => !prev)
  }

  const renderTextField = (name, label, placeholder, isDisabled) => (
    <TextField
      fullWidth
      name={name}
      label={label}
      placeholder={placeholder}
      value={selectedProvider[name] || ''}
      error={error[name]}
      onChange={handleFieldChange(name)}
      disabled={isDisabled}
      outlineColor={
        !isDisabled ? theme.palette.secondary.main : theme.palette.primary.main
      }
    />
  )

  return (
    <Box width="100%">
      <Grid container spacing={2}>
        {/* 1. Fields Section */}
        {isAdding && (
          <Grid item xs={12}>
            {!isManualEntry && (
              <SelectField
                fullWidth
                noMargin
                name="provider"
                label="Select provider from eligibility"
                placeholder="Choose an option"
                options={formatProviderOptions(providers)}
                value={selectedProvider.provider}
                onChange={handleSelectProvider}
                disabled={isManualEntry}
                outlineColor={
                  !isManualEntry
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
              />
            )}
            <Button
              variant="text"
              color={isManualEntry ? 'error' : 'link'}
              onClick={handleManualEntryToggle}
              sx={{ marginTop: '5px' }}
            >
              {isManualEntry
                ? 'Cancel Manual Entry'
                : 'Enter Provider Manually'}
            </Button>
          </Grid>
        )}

        {(isManualEntry || !!selectedProvider.provider || !isAdding) && (
          <>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                fullWidth
                noMargin
                name="type"
                label="Kind of provider"
                options={providerKindOptions}
                value={selectedProvider.type}
                error={error.type}
                onChange={handleFieldChange('type')}
                placeholder="Choose an option"
                disabled={!isEdit}
                outlineColor={
                  isEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                fullWidth
                noMargin
                name="npiType"
                label="NPI Type"
                options={[
                  { value: 'individual', label: 'Individual' },
                  { value: 'organization', label: 'Organization' },
                ]}
                value={selectedProvider.npiType}
                error={error.npiType}
                onChange={handleFieldChange('npiType')}
                placeholder="Choose an option"
                disabled={!isEdit}
                outlineColor={
                  isEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
              />
            </Grid>

            {selectedProvider.npiType === 'organization' && (
              <Grid item xs={6} sm={6} md={3}>
                {renderTextField(
                  'organizationName',
                  'Organization Name',
                  'Organization Name',
                  !isEdit
                )}
              </Grid>
            )}

            {selectedProvider.npiType !== 'organization' && (
              <>
                <Grid item xs={6} sm={6} md={3}>
                  {renderTextField(
                    'firstName',
                    'First Name',
                    'First Name',
                    !isEdit
                  )}
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  {renderTextField(
                    'lastName',
                    'Last Name',
                    'Last Name',
                    !isEdit
                  )}
                </Grid>
              </>
            )}

            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                fullWidth
                noMargin
                name="specialty"
                label="Specialty"
                options={specialtyOptions}
                value={selectedProvider.specialty}
                error={error.specialty}
                onChange={handleFieldChange('specialty')}
                placeholder="Choose an option"
                disabled={!isEdit}
                outlineColor={
                  isEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                fullWidth
                noMargin
                name="taxonomy"
                label="Taxonomy"
                options={taxonomies}
                value={selectedProvider.taxonomy}
                error={error.taxonomy}
                onChange={handleFieldChange('taxonomy')}
                placeholder="Choose an option"
                disabled={!isEdit}
                outlineColor={
                  isEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderTextField('taxId', 'Tax ID', 'Tax ID', !isEdit)}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderTextField('npi', 'NPI', 'NPI', !isEdit)}
            </Grid>
            {/* DentalXchange only takes the license on the rendering provider */}
            {selectedProvider.type === 'RENDERING' && (
              <>
                <Grid item xs={6} sm={6} md={3}>
                  {renderTextField(
                    'licenseNumber',
                    'License Number',
                    'License Number',
                    !isEdit
                  )}
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <SelectField
                    fullWidth
                    noMargin
                    name="licenseState"
                    label="License State"
                    options={usStateOptions}
                    value={selectedProvider.licenseState}
                    error={error.licenseState}
                    onChange={handleFieldChange('licenseState')}
                    placeholder="Choose an option"
                    disabled={!isEdit}
                    outlineColor={
                      isEdit
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6} sm={6} md={3}>
              {renderTextField('address1', 'Address', 'Address', !isEdit)}
            </Grid>
            {selectedProvider?.address2 && (
              <Grid item xs={6} sm={6} md={3}>
                {renderTextField('address2', 'Address 2', 'Address 2', !isEdit)}
              </Grid>
            )}

            <Grid item xs={6} sm={6} md={3}>
              {renderTextField('city', 'City', 'City', !isEdit)}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderTextField('state', 'State', 'State', !isEdit)}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderTextField('zipcode', 'Zipcode', 'Zipcode', !isEdit)}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                fullWidth
                noMargin
                name="kind"
                label="Kind"
                options={[
                  { label: 'Primary', value: 'primary' },
                  { label: 'Secondary', value: 'secondary' },
                ]}
                value={selectedProvider.kind}
                error={error.kind}
                onChange={handleFieldChange('kind')}
                placeholder="Choose an option"
                disabled={!isEdit}
                outlineColor={
                  isEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
              />
            </Grid>
          </>
        )}

        {/* 2. Buttons on Their Own Row */}
        {!viewOnly && (
          <Grid item xs={12}>
            <Box display="flex" flexDirection="row" gap={1}>
              {isEdit ? (
                <>
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<SaveOutlined color="link" />}
                    onClick={handleSave}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Save
                  </Button>

                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<CancelOutlined color="error" />}
                    onClick={handleCancel}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<EditOutlined color="link" />}
                    onClick={onEdit}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="text"
                    disableElevation
                    startIcon={<DeleteOutline color="link" />}
                    onClick={onDelete}
                    sx={{
                      marginBottom: '4px',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: theme.palette.silver.dark,
                      },
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default Provider
